"use client"

import Script from "next/script"
import { usePathname } from "next/navigation"
import { useEffect, useRef } from "react"
import { CookieCategory } from "@/lib/cookies"
import { useCookies } from "@/lib/context/cookie-context"

type FbqFn = (
  command: "init" | "track" | "consent",
  ...args: (string | Record<string, unknown> | undefined)[]
) => void

declare global {
  interface Window {
    fbq?: FbqFn
    _fbq?: FbqFn
  }
}

/**
 * Loads Meta Pixel only when marketing cookie consent is granted.
 * Fires PageView on client-side navigations (App Router SPA).
 */
export function FacebookPixel() {
  const pathname = usePathname()
  const { hasConsent } = useCookies()
  const pixelId = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID?.trim()
  const marketingAllowed = hasConsent(CookieCategory.MARKETING)
  const skipPathEffectOnce = useRef(true)

  useEffect(() => {
    if (typeof window === "undefined" || !marketingAllowed || !pixelId) {
      return
    }
    if (!window.fbq) {
      return
    }
    if (skipPathEffectOnce.current) {
      skipPathEffectOnce.current = false
      return
    }
    window.fbq("track", "PageView")
  }, [pathname, marketingAllowed, pixelId])

  if (!pixelId || !marketingAllowed) {
    return null
  }

  return (
    <>
      <Script id="facebook-pixel" strategy="afterInteractive">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}');
          fbq('track', 'PageView');
        `}
      </Script>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src={`https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </>
  )
}
